import {Injectable} from '@angular/core';
import {AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn} from '@angular/forms';
import {ForbiddenWordService} from './user-registration/forbidden-word.service';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {map, take} from 'rxjs/operators';
import {User} from '@spartacus/core';
import {Observable} from 'rxjs';

export const LATIN_1_REGEX = /[^\u0020-\u007e\u00a0-\u00ff]/g;
export const LATIN_1_WITH_ASCII_REGEX = /[^A-z\d\u00C0-\u00ff\s'\.,-\/#!$%\^&\*;:{}=\-\+_`~()]/g;
export const LATIN_LETTER = /[A-Za-z]/;

@Injectable({
  providedIn: 'root'
})
export class DcpValidators {

  emailValidationRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  constructor(
    private forbiddenWordService: ForbiddenWordService,
    private userFacade: UserAccountFacade,
  ) {
  }

  static onlyLatin1Characters(withAscii: boolean = false): ValidatorFn {
    return ({value}: AbstractControl): ValidationErrors | null => this.onlyLatin1CharactersFunction(value, withAscii);
  }

  static onlyLatin1CharactersFunction(value: string, withAscii = false) {
    let regexToTest = new RegExp(LATIN_1_REGEX);

    if (withAscii) {
      regexToTest = new RegExp(LATIN_1_WITH_ASCII_REGEX);
    }

    return regexToTest.test(value) ? {
      onlyLatin1Characters: {value}
    } : null;
  }

  static checkMatch(field1: string, field2: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const field1Value: string = control.get(field1)?.value?.trim().toLowerCase();
      const field2Value: string = control.get(field2)?.value?.trim().toLowerCase();

      return (field1Value && field2Value && field1Value === field2Value)
        ? {
          [`checkMatch-${field1}:${field2}`]: {field1, field2}
        }
        : null;
    };
  }

  static atLeastOneLetter(): ValidatorFn {
    const regexToTest = new RegExp(LATIN_LETTER);

    return ({value}: AbstractControl): ValidationErrors | null => !value?.length || regexToTest.test(value) ? null : {
      atLeastOneLetter: {value},
    };
  }

  public noForbiddenWords(): ValidatorFn {
    return ({value}: AbstractControl): ValidationErrors | null => this.forbiddenWordService.checkForbiddenWords(value) ? {
      forbiddenWords: {value},
    } : null;
  }

  public sameDomain(): AsyncValidatorFn {
    return ({value}: AbstractControl) => this.sameDomainImpl(value);
  }

  public sameDomainImpl(value): Observable<ValidationErrors | null> {
    return this.userFacade.get().pipe(
      take(1),
      map((user: User) => {
        const isEqual = this.isDomainEqual(user?.email, value);
        return isEqual ? null : {sameDomain: {value: [user?.email, value]}};
      })
    );
  }

  public isDomainEqual(email1: string, email2: string) {
    try {
      const domain1 = email1.match(this.emailValidationRegex)[5];
      const domain2 = email2.match(this.emailValidationRegex)[5];
      return domain1 === domain2;
    } catch (err) {
      return false;
    }
  }

}
